/**
 * Who an @mention will actually reach, decided before a comment is posted.
 *
 * Mention notifications only go to bid members (the server drops the rest).
 * So before posting, the composer sorts the people a body mentions who are
 * NOT on the bid into two groups:
 *  - `addable`: the author may add them to the bid (they manage its team and
 *    the person is in the organisation) — the composer asks first;
 *  - `unreachable`: nobody is adding them, so they won't hear about it — the
 *    composer says so rather than dropping them silently.
 */

import { parseMentionNamesByUserId } from '@/lib/comment-mentions'

export interface MentionedPerson {
  userId: string
  name: string
}

export interface MentionGate {
  addable: MentionedPerson[]
  unreachable: MentionedPerson[]
}

export const EMPTY_MENTION_GATE: MentionGate = { addable: [], unreachable: [] }

/**
 * Mirrors the server's `requireBidTeamManage` (POST /bids/{id}/members): never
 * an org viewer; otherwise an org admin or an owner of this bid. The server
 * re-checks on every request — this only decides what the UI offers.
 */
export function canManageBidTeam(
  orgRole: 'admin' | 'member' | 'viewer',
  bidMembers: ReadonlyArray<{ userId: string; role: string }>,
  currentUserId: string | null,
): boolean {
  if (orgRole === 'viewer') return false
  if (orgRole === 'admin') return true
  return bidMembers.some((m) => m.userId === currentUserId && m.role === 'owner')
}

export function computeMentionGate(
  body: string,
  opts: {
    memberIds: ReadonlySet<string>
    addableIds: ReadonlySet<string>
    currentUserId: string | null
  },
): MentionGate {
  const addable: MentionedPerson[] = []
  const unreachable: MentionedPerson[] = []
  for (const [userId, name] of parseMentionNamesByUserId(body)) {
    if (userId === opts.currentUserId || opts.memberIds.has(userId)) continue
    const person = { userId, name }
    if (opts.addableIds.has(userId)) addable.push(person)
    else unreachable.push(person)
  }
  return { addable, unreachable }
}

/** "Sam", "Sam and Priya", "Sam, Priya and Lee". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** "Add Sam to this bid so they can see and reply?" */
export function addToBidQuestion(people: MentionedPerson[]): string {
  return `Add ${joinNames(people.map((p) => p.name))} to this bid so they can see and reply?`
}

/** "Sam isn't on this bid, so they won't be notified." */
export function notNotifiedNote(people: MentionedPerson[]): string {
  const verb = people.length === 1 ? "isn't" : "aren't"
  return `${joinNames(people.map((p) => p.name))} ${verb} on this bid, so they won't be notified.`
}

/** A person the @mention picker offers: a bid member, or (flagged) a colleague who is not on the bid yet. */
export interface MentionCandidate {
  userId: string
  role: string
  email: string
  fullName: string | null
  avatarUrl: string | null
  /** True for an organisation member who is not on this bid. */
  notOnBid?: boolean
}

/** Picker rows for a query: bid members first, then colleagues not on the bid; at most `limit`. */
export function buildMentionItems(
  bidMembers: ReadonlyArray<MentionCandidate>,
  nonMembers: ReadonlyArray<MentionCandidate>,
  query: string,
  limit = 8,
): MentionCandidate[] {
  const q = query.toLowerCase()
  const matches = (m: MentionCandidate) =>
    !q || (m.fullName?.toLowerCase().includes(q) ?? false) || m.email.toLowerCase().includes(q)
  return [
    ...bidMembers.filter(matches),
    ...nonMembers.filter(matches).map((m) => ({ ...m, notOnBid: true })),
  ].slice(0, limit)
}
